import { useQuery, useInfiniteQuery } from "@tanstack/react-query";
import { publicServerEndpoint } from "../services/helper";

const fetchCollections = async (storeId, source = "featured") => {
  const response = await fetch(
    `${publicServerEndpoint}/v3/collection/store/${storeId}${source ? `?source=${source}` : ""}`,
  );
  if (!response.ok) {
    throw new Error("Failed to fetch collections");
  }
  return response.json();
};

const fetchCollectionsPage = async ({ storeId, pageParam = 1, limit = 5 }) => {
  try {
    const response = await fetch(
      `${publicServerEndpoint}/v3/collection/store/${storeId}?limit=${limit}&page=${pageParam}`,
    );
    if (!response.ok) {
      throw new Error(`Failed to fetch collections: ${response.status} ${response.statusText}`);
    }
    return response.json();
  } catch (error) {
    console.error(`Error fetching collections page ${pageParam}:`, error);
    throw error;
  }
};

const useCollection = (storeId, source = "featured") => {
  return useQuery(["collections", storeId, source], () => fetchCollections(storeId, source), {
    enabled: !!storeId,
  });
};

/**
 * Hook for fetching collections with pagination
 * @param {string} storeId - Store ID to fetch collections for
 * @param {number} limit - Number of collections per page
 * @returns {Object} - React Query infinite query result
 */
const usePaginatedCollections = (storeId, limit = 5) => {
  return useInfiniteQuery(
    ["paginatedCollections", storeId, limit],
    ({ pageParam = 1 }) => fetchCollectionsPage({ storeId, pageParam, limit }),
    {
      enabled: !!storeId,
      getNextPageParam: (lastPage, allPages) => {
        // Use the pagination.pages from the response to determine if there are more pages
        if (!lastPage.pagination || !lastPage.pagination.pages) {
          return undefined; // No pagination info available
        }

        const currentPage = allPages.length;
        const totalPages = lastPage.pagination.pages;

        // Return undefined if we're at the last page to prevent extra API calls
        return currentPage < totalPages ? currentPage + 1 : undefined;
      },
      retry: 2,
      refetchOnWindowFocus: false,
    },
  );
};

export default useCollection;
export { usePaginatedCollections };
